/**
 * ResubscribePage.tsx — /resubscribe, the lane-B confirmation page.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: end-of-term re-subscribe.
 *
 * THIS PAGE IS WHERE THE PATIENT AUTHORISES A CHARGE. A finished term does not
 * renew itself — a new term is a NEW CONTRACT, and the mandate the patient gave
 * at first checkout covered the term they bought. So the admin (or the patient)
 * starts the offer, and this is the screen on which the patient says yes.
 *
 * THE TOKEN COMES OUT OF THE FRAGMENT AND IS SCRUBBED IMMEDIATELY.
 * `#token=…`, never `?token=…`: a query string is written verbatim into
 * load-balancer, WAF and nginx access logs on the first page load and no
 * client-side rewrite retracts it, and the gateway 400s this lane if a query
 * string is present at all. The fragment is read ONCE during the first render
 * and `history.replaceState`d away, so it does not survive in browser history,
 * in a screen-share, or in a URL copied out of the address bar. Every request
 * carries the token in the BODY. (Shared with the pharmacy sign-in lane —
 * `lib/hashToken`.)
 *
 * ONE NEUTRAL FAILURE STATE, NEVER AN ORACLE. Unknown, expired, already used,
 * declined, withdrawn — the server answers all of them with a byte-identical
 * 404 on purpose, so that holding a token tells you nothing about whether it was
 * ever real. This page must not try to tell them apart, and the copy must not
 * imply it can.
 *
 * WHAT CONFIRMING ACTUALLY DOES, and why the copy says so: it places a HOLD on
 * the card already on file. It does NOT buy medicine. A repeat supply of a
 * prescription-only medicine needs a clinician every single time, and the money
 * is only taken when one approves the NEW order — the previous term's approval
 * has nothing to do with it.
 *
 * A NEW TERM IS A NEW CONTRACT, so the shared `CommitmentTerms` gate is asked
 * again here, exactly as at first checkout. Agreeing once did not agree forever.
 *
 * R28: nothing in this file names a treatment. `medicationName` is rendered
 * verbatim from the API response and exists only at runtime.
 *
 * A11y: one `<h1>`, the outcome in a `role="status"` live region, the failure in
 * a `role="alert"` one, and the kit's own focus-visible rings on every control.
 */
import { useEffect, useState } from 'react';
import { previewResubscribe, confirmResubscribe, declineResubscribe } from '../api/orders';
import type { ResubscribeConfirmResult, ResubscribeOfferPreview } from '../api/types';
import { formatAddress, formatPence, formatUtcDate } from '../lib/format';
import { readHashToken, scrubHashFromAddressBar } from '../lib/hashToken';
import { CommitmentTerms } from '../checkout/CommitmentTerms';
import { Alert, Button, Card, Help, PageFrame, Spinner, Title } from '../ui/kit';
import { Row } from '../ui/sections';
import { FAILURE_REASON_COPY_PATIENT, monthsLabel } from './resubscribeCopy';

/**
 * The ONE failure message. Says what to do next without revealing WHICH failure
 * happened — see the module doc. Used for a missing fragment, a 404, and any
 * other error, because distinguishing them is exactly what we are refusing to do.
 */
const UNIFORM_FAILURE =
  'This link is no longer valid. It may have already been used or it may have expired. Please contact us and we will send you a new one.';

type Stage =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'offer'; preview: ResubscribeOfferPreview }
  | { kind: 'confirmed'; result: ResubscribeConfirmResult }
  | { kind: 'declined' };

export function ResubscribePage() {
  // Read the fragment during the FIRST render, before anything can navigate:
  // the scrub below removes it from the URL, so a later read finds nothing.
  const [token] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : readHashToken(window.location.hash),
  );
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  // A failed confirm/decline is shown WITHOUT throwing the patient off the
  // offer — they can simply press again. Distinct from `invalid`, which is
  // terminal.
  const [actionError, setActionError] = useState<string | null>(null);

  // Scrub the live credential out of the address bar the moment we hold it.
  useEffect(() => {
    if (!token) return;
    scrubHashFromAddressBar();
  }, [token]);

  useEffect(() => {
    if (!token) {
      setStage({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    previewResubscribe(token)
      .then((preview) => {
        if (!cancelled) setStage({ kind: 'offer', preview });
      })
      .catch(() => {
        // EVERY failure lands here and renders the SAME state. No retry is
        // offered: a retry button on an enumeration-safe 404 is an invitation
        // to hammer it, and a genuinely valid link works first time.
        if (!cancelled) setStage({ kind: 'invalid' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  function confirm() {
    if (!token || busy) return;
    setBusy(true);
    setActionError(null);
    confirmResubscribe(token)
      .then((result) => {
        setBusy(false);
        setStage({ kind: 'confirmed', result });
      })
      .catch(() => {
        setBusy(false);
        setActionError('Sorry — we could not confirm your plan just now. Please try again.');
      });
  }

  function decline() {
    if (!token || busy) return;
    setBusy(true);
    setActionError(null);
    declineResubscribe(token)
      .then(() => {
        setBusy(false);
        setStage({ kind: 'declined' });
      })
      .catch(() => {
        setBusy(false);
        setActionError('Sorry — we could not record that just now. Please try again.');
      });
  }

  return (
    <PageFrame narrow>
      <Title>Start your next plan</Title>

      {stage.kind === 'loading' ? <Spinner label="Loading your plan…" /> : null}

      {stage.kind === 'invalid' ? (
        <Alert tone="warning" role="alert">
          {UNIFORM_FAILURE}
        </Alert>
      ) : null}

      {stage.kind === 'offer' ? (
        <>
          <Help>
            Here is what your next plan would be. Nothing is charged until a clinician has reviewed and approved
            your treatment.
          </Help>

          {actionError ? (
            <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {actionError}
            </Alert>
          ) : null}

          <Card>
            {/* Every value here is the SERVER'S — including the treatment name.
                Nothing on this screen is derived, defaulted or looked up locally. */}
            <Row label="Treatment" value={stage.preview.medicationName} />
            <Row label="Plan length" value={monthsLabel(stage.preview.months)} />
            <Row label="Monthly price" value={formatPence(stage.preview.monthlyPence)} />
            <Row label="Total for the plan" value={formatPence(stage.preview.totalPence)} />
            {stage.preview.deliveryAddress ? (
              <Row label="Delivered to" value={formatAddress(stage.preview.deliveryAddress)} />
            ) : null}
            <Row label="This offer expires" value={formatUtcDate(stage.preview.expiresAt)} />
          </Card>

          {/* The same commitment gate as first checkout — a new term is a new
              contract, so the tick is required again. */}
          <CommitmentTerms
            months={stage.preview.months}
            monthlyPence={stage.preview.monthlyPence}
            totalPence={stage.preview.totalPence}
            accepted={accepted}
            onChange={setAccepted}
          />

          <div style={{ display: 'grid', gap: 'var(--gps-s-3)', marginTop: 'var(--gps-s-4)' }}>
            <Button type="button" variant="cta" disabled={!accepted || busy} onClick={confirm}>
              {busy ? 'Confirming…' : 'Confirm my new plan'}
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={decline}>
              No thanks
            </Button>
          </div>

          <p style={{ marginTop: 'var(--gps-s-4)', fontSize: '13px', color: 'var(--gps-muted)' }}>
            Confirming reserves the amount above on the card we already hold for you. We only take it once a
            clinician approves your treatment — if they don’t, you pay nothing.
          </p>
        </>
      ) : null}

      {stage.kind === 'confirmed' ? <ConfirmedPanel result={stage.result} /> : null}

      {stage.kind === 'declined' ? (
        <div role="status">
          <Alert tone="info">
            Thank you for letting us know — we have not started a new plan and nothing has been charged.
          </Alert>
        </div>
      ) : null}
    </PageFrame>
  );
}

/**
 * The outcome of a confirm.
 *
 * `payment_action_required` is an honest stall and NOT a failure: no money moved
 * and no half-state exists. It happens when the bank wants the patient to
 * authenticate, when the saved card is declined, or when there is no saved card
 * — three causes, one remedy (pay with any card), which is why they share a
 * screen. The reason is still named, because "something went wrong" gives a
 * patient nothing to act on.
 */
function ConfirmedPanel({ result }: { result: ResubscribeConfirmResult }) {
  const needsPayment = result.state === 'payment_action_required';
  return (
    <div role="status">
      {needsPayment ? (
        <>
          <Alert tone="warning" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {result.failureReason
              ? FAILURE_REASON_COPY_PATIENT[result.failureReason]
              : 'We could not take payment automatically.'}{' '}
            Nothing has been charged. You can finish with any card.
          </Alert>
          {result.paymentLinkUrl ? (
            <a href={result.paymentLinkUrl} rel="noreferrer" style={{ fontWeight: 600, color: 'var(--gps-link)' }}>
              Finish setting up your plan
            </a>
          ) : (
            <Help style={{ marginBottom: 0 }}>We have emailed you a link to finish setting up your plan.</Help>
          )}
        </>
      ) : (
        <>
          <Alert tone="success" style={{ marginBottom: 'var(--gps-s-3)' }}>
            Thank you — your new plan is with a clinician for review.
          </Alert>
          <Help style={{ marginBottom: 0 }}>
            We will be in touch as soon as they have looked at it. Nothing is charged until they approve it.
          </Help>
        </>
      )}
    </div>
  );
}
